import { useState, useMemo, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Search, Upload, X, Trash2, Pencil, Link2, BookOpen, FileText, Video, PlayCircle,
  MessageCircle, Sparkles, Send, Folder as FolderIcon, FolderPlus, ExternalLink, Download,
  Image as ImageIcon, ChevronRight, ArrowLeft,
} from 'lucide-react';
import apiClient, { api } from '../api/client.ts';
import DarkSelect from '../components/DarkSelect.tsx';
import { PdfViewerButton } from '../components/PdfViewer.tsx';
import { EquipmentLinkPicker, equipmentLabel, type BomEquipmentOption } from '../components/EquipmentLinkPicker.tsx';
import { BomSelector } from '../components/BomSelector.tsx';
import { FloatingActionButton } from '../components/FloatingActionButton.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { spinIconOut } from '../utils/iconAnimations.ts';
import '../styles/buttonAnimations.css';

function getProxyUrl(url: string): string {
  const baseUrl = import.meta.env.VITE_API_URL ?? '';
  const token = localStorage.getItem('accessToken');
  return `${baseUrl}/scheduled-tasks/report-proxy?url=${encodeURIComponent(url)}&token=${token}`;
}

// ─── Learning Hub — equipment/repair manual library ────────────────────────────
// Layout reference: mockups/manuals-tutorials-page.html's Option B (#view-b, "Learning Hub").
// Structure/CSS conventions reused directly (hero + search, category tiles, book-spine cards,
// video cards, lightbox); the mockup's placeholder copy ("Help Center", "Contact support",
// ProQure-feature video titles) and sample data do NOT carry over -- this is a real equipment/
// repair-manual library, not a ProQure help page.

type EntryType = 'book' | 'manual' | 'video' | 'link' | 'parts_image';

interface LearningHubEquipmentLink {
  id: number;
  equipment_type: string;
  display_name: string;
  brand: string | null;
  model: string | null;
}

export interface LearningHubEntry {
  id: number;
  facility_id: number;
  org_id: number;
  type: EntryType;
  title: string;
  description: string | null;
  file_url: string | null;
  youtube_video_id: string | null;
  // parts_image only -- the real external parts-list page ("Open link" action). file_url holds
  // the image itself for this type (see migration_v137's comment for why these are two separate
  // columns/fields rather than reusing one for both roles).
  external_link_url: string | null;
  created_by: number | null;
  created_at: string;
  created_by_first_name: string | null;
  created_by_last_name: string | null;
  created_by_username: string | null;
  equipment_link_count: number;
  equipment_links?: LearningHubEquipmentLink[];
  // Only set when the active search term matched extracted file content rather than the title —
  // plain truncated excerpt around the match, populated server-side. Always null for videos
  // (no file content to search) and for non-search list loads.
  content_snippet?: string | null;
  folder_id: number | null;
  folder_name?: string | null;
}

interface LearningHubFolder {
  id: number;
  facility_id: number;
  org_id: number;
  type: EntryType;
  name: string;
  parent_id: number | null;
  created_by: number | null;
  created_at: string;
  entry_count: number;
  subfolder_count: number;
}

const TYPE_LABEL: Record<EntryType, string> = { book: 'Book', manual: 'Manual', video: 'Video', link: 'Link', parts_image: 'Parts Image' };

// Best-effort hostname extraction for a link entry's URL — falls back to the raw URL if parsing
// fails (e.g. a malformed or relative value that somehow made it through).
function urlDomain(url: string | null | undefined): string {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

// Fixed deterministic palette (id-hashed) for book-spine cover gradients -- no equipment-derived
// categories driving color per Chris's ask, just visual variety.
const SPINE_GRADIENTS: [string, string][] = [
  ['#3b82f6', '#1e3a8a'], ['#14b8a6', '#0f766e'], ['#8b5cf6', '#5b21b6'],
  ['#f59e0b', '#b45309'], ['#64748b', '#334155'], ['#ef4444', '#991b1b'],
];
function spineGradient(id: number): string {
  const [a, b] = SPINE_GRADIENTS[id % SPINE_GRADIENTS.length];
  return `linear-gradient(135deg, ${a}, ${b})`;
}

function creatorName(e: LearningHubEntry): string {
  const name = `${e.created_by_first_name ?? ''} ${e.created_by_last_name ?? ''}`.trim();
  return name || e.created_by_username || 'Unknown';
}

// Accepts a full YouTube URL (watch/embed/shorts, any subdomain e.g. m.youtube.com since this
// isn't anchored to string start) or a bare video ID, and returns just the ID. shorts/ was added
// here -- it's real video content under the hood, embeds identically via /embed/VIDEO_ID -- the
// comment above previously claimed "short" was already handled but the regex never had that
// alternative, so a /shorts/ link fell through to the bare-ID fallback and returned the whole URL.
function extractYoutubeId(input: string): string {
  const trimmed = input.trim();
  const match = trimmed.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{6,})/);
  return match ? match[1] : trimmed;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

// ─── Video lightbox — same behavior as the mockup's #lightbox, but the iframe src is built from
// the entry's real youtube_video_id instead of a hardcoded demo video. ───────────────────────
function VideoLightbox({ videoId, title, subtitle, onClose }: {
  videoId: string; title: string; subtitle: string; onClose: () => void;
}) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);
  return ReactDOM.createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(5,8,14,0.88)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 30 }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ width: '100%', maxWidth: 960 }}>
        <div style={{ aspectRatio: '16/9', borderRadius: 14, overflow: 'hidden', background: '#000', border: '1px solid var(--border-default)', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
            style={{ width: '100%', height: '100%', border: 0 }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={title}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 14 }}>
          <div>
            <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>{title}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{subtitle}</div>
          </div>
          <button className="btn btn-ghost combo-cancel-jello" onMouseLeave={spinIconOut} onClick={onClose} style={{ marginLeft: 'auto' }}>
            <X size={14} className="btn-icon" /> Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Add/Edit entry modal ───────────────────────────────────────────────────────────────────
// Broad equipment-scope context passed in when this modal is opened from an item's own Manuals
// tab (Inventory.tsx SupplyDetailTakeover) instead of Learning Hub's own "Upload to Learning Hub"
// FAB. typeLabel is the human-readable form ("Wheelchair") for the scope-option copy below.
export interface EntryEquipmentScope {
  type: string;
  brand: string | null;
  model: string | null;
  typeLabel: string;
}

// Defaults to the broadest scope that still names a specific make ("all models of that make")
// Defaults to the tightest scope the caller actually knows: exact model when known (fixes bug
// where a manual uploaded from one unit's Manuals tab leaked to every other model of the same
// brand — e.g. an Invacare RPL450-1 mast-assembly entry showing up on an Invacare RPL 600-1 unit),
// else brand-wide, else type-wide. The real BomSelector picker (same Type/Brand/Model checkbox
// tree BOM parts already use) still lets this be widened back to a whole brand or type after the fact.
function defaultUsedForTags(scope: EntryEquipmentScope): string[] {
  if (scope.brand && scope.model) return [`${scope.type}:${scope.brand}:${scope.model}`];
  return [scope.brand ? `${scope.type}:${scope.brand}:all` : `${scope.type}:all`];
}

export function EntryFormModal({ mode, initial, equipmentScope, onClose, onSaved }: {
  mode: 'create' | 'edit';
  initial?: LearningHubEntry;
  equipmentScope?: EntryEquipmentScope;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [type, setType] = useState<EntryType>(initial?.type ?? 'manual');
  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [youtubeInput, setYoutubeInput] = useState(initial?.youtube_video_id ?? '');
  const [url, setUrl] = useState(initial?.type === 'link' ? (initial?.file_url ?? '') : '');
  // parts_image: the image is upload-a-file OR paste-a-URL (same toggle convention Inventory.tsx's
  // item-image panel and facility_hvac's photo upload already use), plus a separate external link
  // field -- the real parts page the "Open link" action opens, never the image itself.
  const [imageInputMode, setImageInputMode] = useState<'file' | 'url'>('file');
  const [imageUrlInput, setImageUrlInput] = useState(initial?.type === 'parts_image' ? (initial?.file_url ?? '') : '');
  const [externalLinkUrl, setExternalLinkUrl] = useState(initial?.external_link_url ?? '');
  const [folderId, setFolderId] = useState<string>(initial?.folder_id != null ? String(initial.folder_id) : '');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mouseDownTarget = useRef<EventTarget | null>(null);
  const [usedForTags, setUsedForTags] = useState<string[]>(
    equipmentScope ? defaultUsedForTags(equipmentScope) : []
  );

  // Folder fetching (level-by-level browse + a flat search-all) now lives inside FolderPicker
  // itself, keyed on `type` the same way this used to be -- was a flat top-level-only fetch here
  // before item 1's fix (see FolderPicker below).

  // Same equipment-catalog endpoint EntryDetailModal's exact-equipment picker already uses, just
  // reshaped to BomSelector's { types } shape (route returns { types, entries } unwrapped, not
  // the usual { success, data } envelope).
  const { data: bomEquipmentData = { types: [] } } = useQuery({
    queryKey: ['bom-equipment-types'],
    queryFn: async () => (await api.get('/inventory/bom-equipment')) as unknown as { types: any[] },
    enabled: !!equipmentScope,
  });

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  async function handleSave() {
    if (!title.trim()) { setError('Title is required'); return; }
    if (type === 'video' && !youtubeInput.trim()) { setError('YouTube video URL or ID is required'); return; }
    if (type === 'link' && !url.trim()) { setError('URL is required'); return; }
    if (type === 'parts_image') {
      if (!externalLinkUrl.trim()) { setError('External link is required'); return; }
      const hasImage = imageInputMode === 'file' ? !!file : !!imageUrlInput.trim();
      if (mode === 'create' && !hasImage) { setError('An image (file or URL) is required'); return; }
    }
    if (type !== 'video' && type !== 'link' && type !== 'parts_image' && mode === 'create' && !file) { setError('A file is required for books and manuals'); return; }
    setError(''); setSaving(true);
    try {
      const fd = new FormData();
      fd.append('type', type);
      fd.append('title', title);
      fd.append('description', description);
      fd.append('folder_id', folderId);
      if (type === 'video') fd.append('youtube_video_id', extractYoutubeId(youtubeInput));
      if (type === 'link') fd.append('url', url);
      if (type === 'parts_image') {
        fd.append('external_link_url', externalLinkUrl);
        if (imageInputMode === 'url' && imageUrlInput.trim()) fd.append('image_url', imageUrlInput.trim());
      }
      if (file) fd.append('file', file);
      if (equipmentScope && mode === 'create') {
        fd.append('used_for', JSON.stringify(usedForTags));
      }

      const baseUrl = (import.meta.env.VITE_API_URL as string) || apiClient.defaults.baseURL || '/api';
      const token = localStorage.getItem('accessToken');
      const endpoint = mode === 'create' ? `${baseUrl}/learning-hub` : `${baseUrl}/learning-hub/${initial!.id}`;
      const resp = await fetch(endpoint, {
        method: mode === 'create' ? 'POST' : 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body?.error || 'Save failed');
      }
      onSaved();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return ReactDOM.createPortal(
    <div
      className="modal-overlay"
      style={{ zIndex: 2100 }}
      onMouseDown={(e) => { mouseDownTarget.current = e.target; }}
      onMouseUp={(e) => { if (mouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) onClose(); mouseDownTarget.current = null; }}
    >
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 480, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }}>
        {/* Fixed Header */}
        <div style={{ flexShrink: 0, padding: '20px 24px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Upload size={18} className="modal-icon-intro" style={{ color: 'var(--accent-primary)' }} />
            {mode === 'create' ? 'Add Learning Hub Entry' : 'Edit Entry'}
          </h3>
          <button className="modal-close" onClick={onClose}><X size={18} className="btn-icon" /></button>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0 }}>
          <div className="form-group">
            <label className="form-label">Type</label>
            <DarkSelect
              value={type}
              onChange={(v) => { setType(v as EntryType); setFolderId(''); }}
              options={[{ value: 'book', label: 'Book' }, { value: 'manual', label: 'Manual' }, { value: 'video', label: 'Video' }, { value: 'link', label: 'Link' }, { value: 'parts_image', label: 'Parts Image' }]}
              placeholder="Select type"
            />
            {mode === 'edit' && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Type can't be changed after creation — delete and re-add if needed.</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Title *</label>
            <input className="form-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Rooftop HVAC Unit — Service Manual" />
          </div>
          <div className="form-group">
            <label className="form-label">Folder</label>
            <FolderPicker type={type} value={folderId} onChange={setFolderId} />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-input" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} style={{ resize: 'vertical' }} />
          </div>
          {type === 'video' ? (
            <div className="form-group">
              <label className="form-label">YouTube video URL or ID *</label>
              <input className="form-input" value={youtubeInput} onChange={(e) => setYoutubeInput(e.target.value)} placeholder="https://www.youtube.com/watch?v=…" />
            </div>
          ) : type === 'link' ? (
            <div className="form-group">
              <label className="form-label">URL *</label>
              <input className="form-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/firmware-downgrade-guide" />
            </div>
          ) : type === 'parts_image' ? (
            <>
              <div className="form-group">
                <label className="form-label">External link *</label>
                <input
                  className="form-input"
                  value={externalLinkUrl}
                  onChange={(e) => setExternalLinkUrl(e.target.value)}
                  placeholder="https://southwestmedical.com/replacement-parts/…"
                />
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  The real parts page "Open link" navigates to — a supplier's parts diagram/list, not a PDF.
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Image {mode === 'create' ? '*' : '(leave blank to keep existing)'}</label>
                {/* Upload File / Paste URL toggle -- same convention as Inventory.tsx's item-image
                    panel and facility_hvac's photo upload. Clears the other mode's own value on
                    switch so a leftover file selection can't silently win over a pasted URL (or
                    vice versa) once handleSave builds the FormData. */}
                <div style={{ display: 'flex', width: '100%', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border-default)', marginBottom: 8 }}>
                  {(['file', 'url'] as const).map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => { setImageInputMode(m); if (m === 'file') setImageUrlInput(''); else setFile(null); }}
                      style={{
                        flex: 1, padding: '6px 0', border: 'none', cursor: 'pointer',
                        fontSize: '0.75rem', fontWeight: 500,
                        background: imageInputMode === m ? 'var(--accent-primary)' : 'transparent',
                        color: imageInputMode === m ? '#fff' : 'var(--text-muted)',
                        transition: 'background 0.15s, color 0.15s',
                      }}
                    >
                      {m === 'file' ? 'Upload File' : 'Paste URL'}
                    </button>
                  ))}
                </div>
                {imageInputMode === 'file' ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--text-secondary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '6px 12px' }}>
                      <Upload size={13} />
                      {file ? file.name : 'Choose image'}
                      <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                    </label>
                    {file && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setFile(null)}>Clear</button>}
                  </div>
                ) : (
                  <input
                    className="form-input"
                    type="url"
                    value={imageUrlInput}
                    onChange={(e) => setImageUrlInput(e.target.value)}
                    placeholder="https://example.com/parts-diagram.jpg"
                  />
                )}
                {((imageInputMode === 'file' && file) || (imageInputMode === 'url' && imageUrlInput.trim())) && (
                  <img
                    src={imageInputMode === 'file' && file ? URL.createObjectURL(file) : imageUrlInput.trim()}
                    alt=""
                    style={{ marginTop: 8, maxWidth: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 8, border: '1px solid var(--border-subtle)' }}
                  />
                )}
              </div>
            </>
          ) : (
            <div className="form-group">
              <label className="form-label">File {mode === 'create' ? '*' : '(leave blank to keep existing)'}</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--text-secondary)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '6px 12px' }}>
                  <Upload size={13} />
                  {file ? file.name : 'Choose file (PDF, DOC, DOCX)'}
                  <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                </label>
                {file && (
                  <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }} onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                    <X size={13} />
                  </button>
                )}
              </div>
            </div>
          )}
          {equipmentScope && mode === 'create' && (
            <div className="form-group">
              <label className="form-label">Link to equipment</label>
              <BomSelector
                selected={usedForTags}
                onChange={setUsedForTags}
                equipmentData={bomEquipmentData}
              />
            </div>
          )}
          {error && <div style={{ color: 'var(--accent-danger)', fontSize: '0.8125rem' }}>{error}</div>}
        </div>

        {/* Fixed Footer */}
        <div style={{ flexShrink: 0, padding: '16px 24px 20px', borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost combo-cancel-jello" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary combo2" disabled={saving} onClick={handleSave}>
            {saving ? 'Saving…' : mode === 'create' ? 'Add Entry' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function getFolderPath(folder: LearningHubFolder, all: LearningHubFolder[]): string {
  const parts = [folder.name];
  let cur = folder;
  while (cur.parent_id) {
    const parent = all.find((f) => f.id === cur.parent_id);
    if (!parent) break;
    parts.unshift(parent.name);
    cur = parent;
  }
  return parts.join(' › ');
}

// ─── FolderPicker: search + arbitrary-depth drill-down, single-select ──────────────────────────
// Item 1's actual fix. EntryFormModal's Folder field used to be a flat DarkSelect built off
// GET /learning-hub/folders?type= with no parent_id -- top-level only, per that route's own
// contract, even though learning_hub_folders has supported arbitrary nesting (parent_id, self-
// referencing FK) since migration_v121 and the folder MANAGEMENT UI on the Learning Hub page
// itself already drills into it fine. This was purely a picker-UI gap, not a data-model one.
//
// Checked the two other "search-integrated" pickers already in this app before building a new one
// (per the request, reuse-over-rebuild): BomSelector (Inventory.tsx/BomSelector.tsx, used for BOM
// "Used For" tagging) is hardcoded to exactly 3 levels -- Type -> Brand -> Model -- baked into its
// own two drill-state variables, not generic recursion. LocationSelector (Inventory.tsx, the
// "Edit Lifts"-style equipment form's own Location field) is hardcoded to exactly 2 -- Area ->
// Sub-location. Neither is a generic arbitrary-depth tree despite both being search-integrated,
// and folders need arbitrary depth. The one truly generic drill-down already in this codebase,
// CategoryPicker (Inventory.tsx, catalogue item Category field -- real stack-based recursion, no
// depth limit), has no search box at all and expects its whole tree preloaded as a flat prop
// rather than being fetched itself. This component is the actual missing piece: CategoryPicker's
// unlimited-depth breadcrumb/stack navigation, with BomSelector/LocationSelector's search bar
// added on top -- fetches its own flat "all folders for this type" list (the all=true branch
// added to GET /learning-hub/folders for this, same prefetch-everything-for-search convention
// those two already use) rather than receiving one as a prop, since folders aren't otherwise
// loaded anywhere else in this modal the way catalogue categories are elsewhere in Inventory.tsx.
function FolderPicker({ type, value, onChange }: { type: EntryType; value: string; onChange: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [stack, setStack] = useState<number[]>([]);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top?: number; bottom?: number; left: number; width: number }>({ left: 0, width: 0 });

  const { data: allRes } = useQuery({
    queryKey: ['learning-hub-folders-all', type],
    queryFn: () => api.get<LearningHubFolder[]>('/learning-hub/folders', { type, all: 'true' }),
  });
  const allFolders = allRes?.data ?? [];

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (
        containerRef.current && !containerRef.current.contains(e.target as Node) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target as Node))
      ) { setOpen(false); setStack([]); setSearch(''); }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function openDropdown() {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const maxDropdownH = 300;
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < maxDropdownH) {
        setDropdownPos({ bottom: window.innerHeight - rect.top + 2, left: rect.left, width: rect.width });
      } else {
        setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
      }
    }
    setOpen((prev) => !prev);
  }

  function selectFolder(id: number | null) {
    onChange(id === null ? '' : String(id));
    setOpen(false);
    setStack([]);
    setSearch('');
  }

  const currentParentId = stack.length > 0 ? stack[stack.length - 1] : null;
  const currentLevel = allFolders.filter((f) => (f.parent_id ?? null) === currentParentId);
  const breadcrumb = stack.map((id) => allFolders.find((f) => f.id === id)?.name ?? '').filter(Boolean);
  const hasChildren = (id: number) => allFolders.some((f) => f.parent_id === id);

  const selectedFolder = value ? allFolders.find((f) => String(f.id) === value) : null;
  const displayLabel = selectedFolder ? getFolderPath(selectedFolder, allFolders) : 'Uncategorized';

  const q = search.trim().toLowerCase();
  const searchResults = q ? allFolders.filter((f) => f.name.toLowerCase().includes(q)) : [];

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={openDropdown}
        style={{
          width: '100%', padding: '9px 12px',
          background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-md)', color: selectedFolder ? 'var(--text-primary)' : 'var(--text-muted)',
          textAlign: 'left', cursor: 'pointer',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14,
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <FolderIcon size={13} />{displayLabel}
        </span>
        <span style={{ color: 'var(--text-muted)', fontSize: 12, flexShrink: 0 }}>▾</span>
      </button>

      {open && ReactDOM.createPortal(
        <div ref={dropdownRef} style={{
          position: 'fixed',
          ...(dropdownPos.top !== undefined ? { top: dropdownPos.top } : { bottom: dropdownPos.bottom }),
          left: dropdownPos.left, width: dropdownPos.width, minWidth: 260,
          zIndex: 10001, background: 'var(--bg-elevated)',
          border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8,
          color: 'var(--text-primary)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          display: 'flex', flexDirection: 'column', maxHeight: 300,
        }}>
          {/* Search bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '0.5px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
            <Search size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <input
              autoFocus
              placeholder="Search folders…"
              value={search}
              onMouseDown={(e) => e.stopPropagation()}
              onChange={(e) => setSearch(e.target.value)}
              style={{ fontSize: 12, background: 'transparent', border: 'none', outline: 'none', flex: 1, color: 'var(--text-primary)' }}
            />
          </div>

          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {q ? (
              // Search results -- flat, any depth, full breadcrumb path shown per match since
              // results can come from anywhere in the tree, not just the current level.
              searchResults.length === 0 ? (
                <div style={{ padding: '16px 10px', fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>No folders found</div>
              ) : (
                searchResults.map((f) => (
                  <div
                    key={f.id}
                    onMouseDown={(e) => { e.stopPropagation(); selectFolder(f.id); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', cursor: 'pointer', background: String(f.id) === value ? 'rgba(59,130,246,0.12)' : 'transparent' }}
                    onMouseEnter={(e) => { if (String(f.id) !== value) (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = String(f.id) === value ? 'rgba(59,130,246,0.12)' : 'transparent'; }}
                  >
                    <FolderIcon size={13} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
                    <span style={{ fontSize: 13, color: String(f.id) === value ? 'var(--accent-primary)' : 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {getFolderPath(f, allFolders)}
                    </span>
                  </div>
                ))
              )
            ) : (
              <>
                {/* Breadcrumb + back, same drill-down UX CategoryPicker already established */}
                {breadcrumb.length > 0 && (
                  <div style={{ padding: '5px 12px', fontSize: 11, color: 'var(--text-muted)', borderBottom: '1px solid var(--border-subtle)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    All{breadcrumb.map((b, i) => <span key={i}> › {b}</span>)}
                  </div>
                )}
                {stack.length > 0 && (
                  <button
                    type="button"
                    onMouseDown={(e) => { e.stopPropagation(); setStack((s) => s.slice(0, -1)); }}
                    style={{ width: '100%', padding: '8px 12px', background: 'none', border: 'none', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer', textAlign: 'left', fontSize: 13, color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <ArrowLeft size={13} /> Back
                  </button>
                )}
                {/* Uncategorized / select-current-branch option */}
                <div
                  onMouseDown={(e) => { e.stopPropagation(); selectFolder(currentParentId); }}
                  style={{
                    padding: '8px 12px', cursor: 'pointer', fontSize: 13, fontStyle: 'italic',
                    borderBottom: '1px solid var(--border-subtle)',
                    color: value === (currentParentId !== null ? String(currentParentId) : '') ? 'var(--accent-primary)' : 'var(--text-secondary)',
                    background: value === (currentParentId !== null ? String(currentParentId) : '') ? 'rgba(59,130,246,0.1)' : 'transparent',
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = value === (currentParentId !== null ? String(currentParentId) : '') ? 'rgba(59,130,246,0.1)' : 'transparent'; }}
                >
                  {currentParentId === null ? 'Uncategorized (top level)' : `Select "${breadcrumb[breadcrumb.length - 1]}"`}
                  {value === (currentParentId !== null ? String(currentParentId) : '') && <span style={{ marginLeft: 8, color: 'var(--accent-success)' }}>✓</span>}
                </div>
                {currentLevel.map((f) => (
                  <div
                    key={f.id}
                    onMouseDown={(e) => { e.stopPropagation(); if (hasChildren(f.id)) { setStack((s) => [...s, f.id]); } else { selectFolder(f.id); } }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', cursor: 'pointer',
                      background: String(f.id) === value ? 'rgba(59,130,246,0.12)' : 'transparent',
                      color: String(f.id) === value ? 'var(--accent-primary)' : 'var(--text-primary)',
                      borderBottom: '1px solid var(--border-subtle)',
                    }}
                    onMouseEnter={(e) => { if (String(f.id) !== value) (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-hover)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = String(f.id) === value ? 'rgba(59,130,246,0.12)' : 'transparent'; }}
                  >
                    <FolderIcon size={13} />
                    <span style={{ flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                    {String(f.id) === value && !hasChildren(f.id) && <span style={{ color: 'var(--accent-success)', fontSize: 12 }}>✓</span>}
                    {hasChildren(f.id) && <ChevronRight size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />}
                  </div>
                ))}
                {currentLevel.length === 0 && (
                  <div style={{ padding: '16px 10px', fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>No subfolders</div>
                )}
              </>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── Entry detail modal — view + Manager+ edit/delete + equipment linking ──────────────────────
export function EntryDetailModal({ entryId, canManage, onClose, onEdit, onDeleted, onPlayVideo }: {
  entryId: number;
  canManage: boolean;
  onClose: () => void;
  onEdit: (entry: LearningHubEntry) => void;
  onDeleted: () => void;
  onPlayVideo: (entry: LearningHubEntry) => void;
}) {
  const qc = useQueryClient();
  const [linking, setLinking] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // parts_image zoom -- same simple click-to-close overlay pattern Inventory.tsx's supply-photo
  // lightbox already uses, just local to this modal instead of a page-level piece of state.
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const { data: entry, isLoading, refetch } = useQuery({
    queryKey: ['learning-hub-entry', entryId],
    queryFn: () => api.get<LearningHubEntry>(`/learning-hub/${entryId}`),
  });
  const e = entry?.data;

  const { data: equipOptionsRaw } = useQuery({
    queryKey: ['bom-equipment-options'],
    queryFn: async () => {
      const res = await api.get('/inventory/bom-equipment');
      return ((res as any).entries ?? []) as BomEquipmentOption[];
    },
    enabled: canManage,
  });
  const linkedIds = new Set((e?.equipment_links ?? []).map((l) => l.id));
  const equipOptions = (equipOptionsRaw ?? []).filter((o) => !linkedIds.has(o.id));

  async function handleLink(equipmentId: number) {
    setLinking(true);
    try {
      await api.post(`/learning-hub/${entryId}/equipment-links`, { equipment_id: equipmentId });
      await refetch();
    } catch { /* surfaced via refetch staying unchanged */ }
    setLinking(false);
  }

  async function handleUnlink(equipmentId: number) {
    await api.delete(`/learning-hub/${entryId}/equipment-links/${equipmentId}`);
    await refetch();
  }

  async function handleDelete() {
    setDeleting(true);
    try {
      await api.delete(`/learning-hub/${entryId}`);
      qc.invalidateQueries({ queryKey: ['learning-hub'] });
      onDeleted();
    } finally {
      setDeleting(false);
    }
  }

  return <>
    {ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 2000 }} onMouseDown={(ev) => { if (ev.target === ev.currentTarget) onClose(); }}>
      {/* Item 8: fixed header + scrollable body + fixed footer, same three-part split
          EntryFormModal (this same file) already uses -- was a single scrolling block before
          (maxHeight:85vh + overflowY:auto on the whole .modal, header and Edit/Delete footer
          included), so a long "Linked equipment" list or description could scroll the Edit/
          Delete buttons out of view entirely rather than leaving them reachable at the bottom. */}
      <div className="modal" style={{ maxWidth: 560, width: '95vw', maxHeight: '85vh', padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={(ev) => ev.stopPropagation()}>
        {isLoading || !e ? (
          <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-muted)' }}>Loading…</div>
        ) : (
          <>
            {/* Fixed header */}
            <div style={{ flexShrink: 0, padding: '20px 24px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {e.type === 'video' ? <Video size={16} style={{ color: '#fb923c' }} /> : e.type === 'link' ? <ExternalLink size={16} style={{ color: '#94a3b8' }} /> : e.type === 'parts_image' ? <ImageIcon size={16} style={{ color: '#c084fc' }} /> : e.type === 'book' ? <BookOpen size={16} style={{ color: '#60a5fa' }} /> : <FileText size={16} style={{ color: '#34d399' }} />}
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>{TYPE_LABEL[e.type]}</span>
                </div>
                <h3 style={{ margin: '6px 0 0', fontSize: '1.2rem', fontWeight: 800 }}>{e.title}</h3>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  Added by {creatorName(e)} · {fmtDate(e.created_at)}
                  {e.folder_name && <> · <FolderIcon size={11} style={{ verticalAlign: -1 }} /> {e.folder_name}</>}
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
            </div>

            {/* Scrollable body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', minHeight: 0 }}>
            {e.description && <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', lineHeight: 1.6, marginBottom: 16 }}>{e.description}</p>}

            {e.type === 'video' ? (
              <button className="btn btn-primary combo2" onMouseLeave={spinIconOut} onClick={() => onPlayVideo(e)} style={{ marginBottom: 18 }}>
                <PlayCircle size={14} className="btn-icon" /> Watch video
              </button>
            ) : e.type === 'link' ? (
              e.file_url && (
                <a href={e.file_url} target="_blank" rel="noopener noreferrer" className="btn btn-primary combo2" style={{ marginBottom: 18, textDecoration: 'none' }}>
                  <ExternalLink size={14} /> Open link
                </a>
              )
            ) : e.type === 'parts_image' ? (
              <div style={{ marginBottom: 18 }}>
                {/* Thumbnail -- click to zoom (lightbox below), separate from "Open link" which
                    navigates to the real external parts page, not the image itself. */}
                {e.file_url && (
                  <img
                    src={e.file_url}
                    alt={e.title}
                    onClick={() => setZoomedImage(e.file_url)}
                    style={{
                      display: 'block', width: '100%', maxHeight: 280, objectFit: 'contain',
                      borderRadius: 10, cursor: 'zoom-in', background: 'var(--bg-elevated)',
                      border: '1px solid var(--border-subtle)', marginBottom: 10,
                    }}
                  />
                )}
                {e.external_link_url && (
                  <a href={e.external_link_url} target="_blank" rel="noopener noreferrer" className="btn btn-primary combo2" style={{ textDecoration: 'none' }}>
                    <ExternalLink size={14} /> Open link
                  </a>
                )}
              </div>
            ) : e.file_url ? (
              <div style={{ marginBottom: 18, display: 'flex', gap: 8 }}>
                <PdfViewerButton
                  pdfUrl={`${getProxyUrl(e.file_url)}&filename=${encodeURIComponent(e.title)}`}
                  label={e.type === 'book' ? 'Open book' : 'Open manual'}
                  triggerClassName="btn btn-primary combo2"
                  triggerIcon={<FileText size={14} />}
                />
                <a
                  href={`${getProxyUrl(e.file_url)}&download=true&filename=${encodeURIComponent(e.title)}`}
                  download
                  className="btn btn-ghost combo1"
                >
                  <Download size={14} /> Save
                </a>
              </div>
            ) : null}

            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>
                <Link2 size={13} /> Linked equipment
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: canManage ? 12 : 0 }}>
                {(e.equipment_links ?? []).length === 0 && <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Not linked to any equipment yet.</span>}
                {(e.equipment_links ?? []).map((link) => (
                  <span key={link.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 999, fontSize: '0.78rem', background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)', color: '#93c5fd' }}>
                    {equipmentLabel(link)}
                    {canManage && (
                      <button onClick={() => handleUnlink(link.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex', padding: 0 }} title="Unlink">
                        <X size={12} />
                      </button>
                    )}
                  </span>
                ))}
              </div>
              {canManage && (
                <EquipmentLinkPicker entries={equipOptions} onSelect={handleLink} disabled={linking} />
              )}
            </div>
            </div>
            {/* Fixed footer -- was inside the scrollable area before, so a long "Linked
                equipment" list could push Edit/Delete out of view below the fold. */}
            {canManage && (
              <div style={{ flexShrink: 0, display: 'flex', gap: 8, padding: '16px 24px', borderTop: '1px solid var(--border-subtle)' }}>
                <button className="btn btn-ghost combo1" onClick={() => onEdit(e)}>
                  <Pencil size={13} /> Edit
                </button>
                {deleteConfirm ? (
                  <>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', alignSelf: 'center' }}>Delete this entry?</span>
                    <button className="btn btn-danger" disabled={deleting} onClick={handleDelete}>{deleting ? 'Deleting…' : 'Confirm'}</button>
                    <button className="btn btn-ghost combo-cancel-jello" onClick={() => setDeleteConfirm(false)}>Cancel</button>
                  </>
                ) : (
                  <button className="combo3-danger" style={{ marginLeft: 'auto', padding: '7px 13px', borderRadius: 8, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.83rem', fontWeight: 600, cursor: 'pointer' }} onClick={() => setDeleteConfirm(true)}>
                    <Trash2 size={13} /> Delete
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body
    )}
    {zoomedImage && ReactDOM.createPortal(
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}
        onClick={() => setZoomedImage(null)}
      >
        <img
          src={zoomedImage}
          alt=""
          onClick={(ev) => ev.stopPropagation()}
          style={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', borderRadius: 8, cursor: 'default' }}
        />
      </div>,
      document.body
    )}
  </>;
}

// ─── AI chat over Learning Hub content — RAG against learning-hub.ts's POST /chat, which
// searches book/manual content (Phase 2) and grounds the answer in the matched excerpts. ───────
interface ChatSource { id: number; title: string; type: EntryType; }
interface ChatMessage { role: 'user' | 'assistant'; content: string; sources?: ChatSource[]; error?: boolean; }

// Long-running AI request — VITE_API_URL directly per CLAUDE_RULES.md (Vercel proxy has a 10s
// timeout), same fetch+bearer-token pattern EntryFormModal.handleSave already uses in this file.
async function sendLearningHubChat(question: string, priorMessages: ChatMessage[]): Promise<{ answer: string; sources: ChatSource[] }> {
  const baseUrl = (import.meta.env.VITE_API_URL as string) || apiClient.defaults.baseURL || '/api';
  const token = localStorage.getItem('accessToken');
  const resp = await fetch(`${baseUrl}/learning-hub/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      question,
      history: priorMessages.map((m) => ({ role: m.role, content: m.content })),
    }),
  });
  const body = await resp.json().catch(() => ({}));
  if (!resp.ok || !body?.success) {
    throw new Error(body?.error || 'Chat request failed');
  }
  return { answer: body.answer, sources: body.sources ?? [] };
}

// ─── Floating AI chat — Enterprise-tier only (same isEnterprise gate pattern already used in
// Inventory.tsx/Projects.tsx/Tasks.tsx/Vendors.tsx: orgPlanTier === 'enterprise' || role ===
// 'admin'); the /chat endpoint itself re-checks this server-side too. ──────────────────────────
function AiChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, open]);

  async function handleSend() {
    const question = input.trim();
    if (!question || sending) return;
    const priorMessages = messages;
    setMessages((m) => [...m, { role: 'user', content: question }]);
    setInput('');
    setSending(true);
    try {
      const { answer, sources } = await sendLearningHubChat(question, priorMessages);
      setMessages((m) => [...m, { role: 'assistant', content: answer, sources }]);
    } catch (e: unknown) {
      setMessages((m) => [...m, { role: 'assistant', content: e instanceof Error ? e.message : 'Something went wrong — please try again.', error: true }]);
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <button
        className="combo-jello-spin"
        data-tint="purple"
        onMouseLeave={spinIconOut}
        onClick={() => setOpen((o) => !o)}
        title="Ask AI about your manuals"
        style={{
          position: 'fixed', bottom: 28, right: 28, zIndex: 500,
          width: 54, height: 54, borderRadius: 999,
          background: 'rgba(139,92,246,0.16)', border: '1px solid rgba(139,92,246,0.45)',
          color: '#c4b5fd', display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', boxShadow: '0 8px 24px rgba(0,0,0,0.35)',
        }}
      >
        {open ? <X size={22} className="btn-icon" /> : <MessageCircle size={22} className="btn-icon" />}
      </button>
      {open && ReactDOM.createPortal(
        <div style={{
          position: 'fixed', bottom: 96, right: 28, zIndex: 500,
          width: 340, maxWidth: '90vw', maxHeight: 480, borderRadius: 16,
          background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
          boxShadow: '0 20px 50px rgba(0,0,0,0.5)', overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <Sparkles size={16} className="modal-icon-intro" style={{ color: '#a78bfa' }} />
            <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Ask about your manuals</span>
          </div>

          <div ref={scrollRef} style={{ flex: 1, minHeight: 180, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {messages.length === 0 && (
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: 24, lineHeight: 1.6 }}>
                Ask a question about your uploaded manuals and books — e.g. "what's the reset procedure for the rooftop HVAC unit?"
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '88%' }}>
                <div style={{
                  padding: '8px 12px', borderRadius: 12, fontSize: '0.82rem', lineHeight: 1.55,
                  whiteSpace: 'pre-wrap',
                  background: m.role === 'user' ? 'rgba(59,130,246,0.18)' : m.error ? 'rgba(239,68,68,0.1)' : 'rgba(255,255,255,0.06)',
                  color: m.error ? '#f87171' : 'var(--text-primary)',
                  border: `1px solid ${m.role === 'user' ? 'rgba(59,130,246,0.3)' : m.error ? 'rgba(239,68,68,0.3)' : 'var(--border-subtle)'}`,
                }}>
                  {m.content}
                </div>
                {m.sources && m.sources.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 5 }}>
                    {m.sources.map((s) => (
                      <span key={s.id} style={{ fontSize: '0.65rem', padding: '2px 7px', borderRadius: 999, background: 'rgba(139,92,246,0.14)', color: '#c4b5fd', border: '1px solid rgba(139,92,246,0.3)' }}>
                        {s.title}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {sending && (
              <div style={{ alignSelf: 'flex-start', fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Thinking…</div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid var(--border-subtle)', flexShrink: 0 }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Ask about your manuals…"
              disabled={sending}
              style={{ flex: 1, minWidth: 0, background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-default)', borderRadius: 8, padding: '8px 10px', color: 'var(--text-primary)', fontSize: '0.82rem', outline: 'none' }}
            />
            <button
              className="combo-jello-spin"
              data-tint="purple"
              onMouseLeave={spinIconOut}
              onClick={handleSend}
              disabled={sending || !input.trim()}
              title="Send"
              style={{
                width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                background: 'rgba(139,92,246,0.16)', border: '1px solid rgba(139,92,246,0.4)', color: '#c4b5fd',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: sending || !input.trim() ? 'default' : 'pointer',
                opacity: sending || !input.trim() ? 0.5 : 1,
              }}
            >
              <Send size={15} className="btn-icon" />
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// ─── Generic confirm modal — used for folder delete (CLAUDE_RULES.md: never window.confirm(),
// always a custom dark modal). ──────────────────────────────────────────────────────────────
function ConfirmModal({ title, message, confirmLabel, danger, onConfirm, onClose }: {
  title: string; message: string; confirmLabel: string; danger?: boolean;
  onConfirm: () => Promise<void> | void; onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  async function handleConfirm() {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  }

  return ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 2200 }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 380, width: '95vw' }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 10px', fontSize: '1.05rem', fontWeight: 700 }}>{title}</h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: 1.5, margin: 0 }}>{message}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="btn btn-ghost combo-cancel-jello" onClick={onClose}>Cancel</button>
          <button className={danger ? 'btn btn-danger' : 'btn btn-primary combo2'} disabled={busy} onClick={handleConfirm}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Folder create/rename modal ─────────────────────────────────────────────────────────────
function FolderModal({ mode, type, initial, parentId, onClose, onSaved }: {
  mode: 'create' | 'rename';
  type: EntryType;
  initial?: LearningHubFolder;
  parentId: number | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  async function handleSave() {
    if (!name.trim()) { setError('Folder name is required'); return; }
    setError(''); setSaving(true);
    try {
      if (mode === 'create') {
        await api.post('/learning-hub/folders', { type, name: name.trim(), parent_id: parentId });
      } else {
        await api.put(`/learning-hub/folders/${initial!.id}`, { name: name.trim() });
      }
      onSaved();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return ReactDOM.createPortal(
    <div className="modal-overlay" style={{ zIndex: 2100 }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" style={{ maxWidth: 380, width: '95vw' }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 18px', fontSize: '1.0625rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
          <FolderIcon size={18} className="modal-icon-intro" style={{ color: 'var(--accent-primary)' }} />
          {mode === 'create' ? `New ${TYPE_LABEL[type]} Folder` : 'Rename Folder'}
        </h3>
        <div className="form-group">
          <label className="form-label">Folder name *</label>
          <input
            className="form-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
            placeholder="e.g. Ice Machine Cleaning"
            autoFocus
          />
        </div>
        {error && <div style={{ color: 'var(--accent-danger)', fontSize: '0.8125rem', marginTop: 8 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 22 }}>
          <button className="btn btn-ghost combo-cancel-jello" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary combo2" disabled={saving} onClick={handleSave}>
            {saving ? 'Saving…' : mode === 'create' ? 'Create Folder' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Folder tile — shown in a type's browse view alongside "Uncategorized" entries. Sized to
// match the same footprint as its sibling entry cards (VideoTileCard for video folders,
// BookSpineCard for book/manual folders) rather than a small, undersized tile. Manager+ gets
// inline rename/delete icon buttons using the shared combo-jello-spin/data-tint mechanics. ─────
function FolderTile({ folder, type, previewVideoId, canManage, onOpen, onRename, onDelete }: {
  folder: LearningHubFolder;
  type: EntryType;
  previewVideoId?: string | null;
  canManage: boolean;
  onOpen: () => void;
  onRename: () => void;
  onDelete: () => void;
}) {
  const manageButtons = canManage && (
    <div style={{ display: 'flex', gap: 2 }} onClick={(e) => e.stopPropagation()}>
      <button
        className="combo-jello-spin" data-tint="neutral" onMouseLeave={spinIconOut} onClick={onRename} title="Rename folder"
        style={{ width: 26, height: 26, borderRadius: 999, background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
      >
        <Pencil size={12} className="btn-icon" />
      </button>
      <button
        className="combo-jello-spin" data-tint="red" onMouseLeave={spinIconOut} onClick={onDelete} title="Delete folder"
        style={{ width: 26, height: 26, borderRadius: 999, background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
      >
        <Trash2 size={12} className="btn-icon" />
      </button>
    </div>
  );

  if (type === 'video') {
    return (
      <div
        onClick={onOpen}
        style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid var(--border-subtle)', background: 'rgba(255,255,255,0.02)', cursor: 'pointer', transition: 'all 0.16s' }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = 'var(--border-default)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = 'var(--border-subtle)'; }}
      >
        <div style={{ aspectRatio: '16/9', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: previewVideoId ? '#000' : spineGradient(folder.id) }}>
          {previewVideoId && (
            <img
              src={`https://img.youtube.com/vi/${previewVideoId}/hqdefault.jpg`}
              alt=""
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
          )}
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} />
          <div style={{ position: 'absolute', top: 9, right: 9, zIndex: 2 }}>{manageButtons}</div>
          {/* Item-count badge — same bottom-right position a video duration badge would sit in,
              so the folder card reads as "a folder", not "a video", at a glance. */}
          <div style={{ position: 'absolute', bottom: 8, right: 8, zIndex: 2, background: 'rgba(0,0,0,0.65)', color: '#fff', fontSize: '0.65rem', fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>
            {folder.entry_count} item{folder.entry_count === 1 ? '' : 's'}
          </div>
          <div style={{ width: 52, height: 52, borderRadius: 999, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
            <FolderIcon size={24} style={{ color: '#fff' }} />
          </div>
        </div>
        {/* Folder-accent metadata bar — purple tint, distinct from VideoTileCard's plain
            background, so folder cards read apart from video cards even at a glance. */}
        <div style={{ padding: '13px 15px', background: 'rgba(139,92,246,0.08)', borderTop: '1px solid rgba(139,92,246,0.15)' }}>
          <div style={{ fontWeight: 600, fontSize: '0.88rem', lineHeight: 1.3 }}>{folder.name}</div>
          <div style={{ fontSize: '0.7rem', color: '#c4b5fd', marginTop: 5 }}>
            Folder{folder.subfolder_count > 0 && ` · ${folder.subfolder_count} subfolder${folder.subfolder_count === 1 ? '' : 's'}`}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onOpen}
      style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border-subtle)', background: 'rgba(255,255,255,0.02)', cursor: 'pointer', transition: 'all 0.16s' }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = 'var(--border-default)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = 'var(--border-subtle)'; }}
    >
      <div style={{ height: 130, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden', background: spineGradient(folder.id) }}>
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, background: 'rgba(0,0,0,0.25)' }} />
        <div style={{ position: 'absolute', top: 9, right: 9, zIndex: 2 }}>{manageButtons}</div>
        <div style={{ position: 'absolute', bottom: 8, right: 8, zIndex: 2, background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: '0.62rem', fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>
          {folder.entry_count} item{folder.entry_count === 1 ? '' : 's'}
        </div>
        <FolderIcon size={32} style={{ color: 'rgba(255,255,255,0.92)' }} />
      </div>
      {/* Folder-accent metadata bar — purple tint, distinct from BookSpineCard's plain background. */}
      <div style={{ padding: '11px 14px', background: 'rgba(139,92,246,0.08)', borderTop: '1px solid rgba(139,92,246,0.15)' }}>
        <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>{folder.name}</div>
        <div style={{ fontSize: '0.68rem', color: '#c4b5fd', marginTop: 3 }}>
          Folder{folder.subfolder_count > 0 && ` · ${folder.subfolder_count} subfolder${folder.subfolder_count === 1 ? '' : 's'}`}
        </div>
      </div>
    </div>
  );
}

// ─── Category tiles ─────────────────────────────────────────────────────────────────────────
const CATEGORY_TILES: { type: EntryType; icon: string; label: string; desc: string; bg: string }[] = [
  { type: 'book', icon: '📚', label: 'Books', desc: 'Reference books & guides', bg: 'rgba(59,130,246,0.16)' },
  { type: 'manual', icon: '📘', label: 'Manuals', desc: 'Equipment & repair manuals', bg: 'rgba(20,184,166,0.16)' },
  { type: 'video', icon: '🎬', label: 'Videos', desc: 'Video walkthroughs', bg: 'rgba(245,158,11,0.16)' },
  { type: 'link', icon: '🔗', label: 'Links', desc: 'External links & downloads', bg: 'rgba(100,116,139,0.18)' },
  { type: 'parts_image', icon: '🖼️', label: 'Parts Images', desc: 'Diagrams linking out to supplier parts pages', bg: 'rgba(168,85,247,0.16)' },
];

// ─── Book-spine card (books + manuals) ─────────────────────────────────────────────────────
function BookSpineCard({ entry, onClick }: { entry: LearningHubEntry; onClick: () => void }) {
  return (
    <div
      onClick={onClick}
      style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid var(--border-subtle)', background: 'rgba(255,255,255,0.02)', cursor: 'pointer', transition: 'all 0.16s' }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = 'var(--border-default)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = 'var(--border-subtle)'; }}
    >
      <div style={{ height: 130, display: 'flex', alignItems: 'flex-end', padding: 14, position: 'relative', overflow: 'hidden', background: spineGradient(entry.id) }}>
        <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, background: 'rgba(0,0,0,0.25)' }} />
        <div style={{ fontWeight: 700, fontSize: '0.9rem', lineHeight: 1.25, position: 'relative', zIndex: 1, textShadow: '0 1px 4px rgba(0,0,0,0.4)' }}>{entry.title}</div>
      </div>
      <div style={{ padding: '11px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: '0.56rem', fontWeight: 800, padding: '2px 7px', borderRadius: 5, letterSpacing: '0.03em', background: entry.type === 'book' ? 'rgba(59,130,246,0.16)' : 'rgba(20,184,166,0.16)', color: entry.type === 'book' ? '#60a5fa' : '#2dd4bf' }}>
          {TYPE_LABEL[entry.type].toUpperCase()}
        </span>
        {entry.equipment_link_count > 0 && (
          <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Link2 size={11} /> {entry.equipment_link_count}
          </span>
        )}
      </div>
      {entry.content_snippet && (
        <div style={{ padding: '0 14px 12px', fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.45, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any }}>
          {entry.content_snippet}
        </div>
      )}
    </div>
  );
}

// ─── Video card ──────────────────────────────────────────────────────────────────────────────
function VideoTileCard({ entry, onPlay, onDetails }: { entry: LearningHubEntry; onPlay: () => void; onDetails: () => void }) {
  return (
    <div
      onClick={onPlay}
      style={{ borderRadius: 14, overflow: 'hidden', border: '1px solid var(--border-subtle)', background: 'rgba(255,255,255,0.02)', cursor: 'pointer', transition: 'all 0.16s' }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = 'var(--border-default)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = 'var(--border-subtle)'; }}
    >
      <div style={{ aspectRatio: '16/9', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', background: entry.youtube_video_id ? '#000' : spineGradient(entry.id) }}>
        {entry.youtube_video_id && (
          <img
            src={`https://img.youtube.com/vi/${entry.youtube_video_id}/hqdefault.jpg`}
            alt=""
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)' }} />
        <button
          onClick={(e) => { e.stopPropagation(); onDetails(); }}
          title="Details / link equipment"
          style={{ position: 'absolute', top: 9, right: 9, zIndex: 2, width: 26, height: 26, borderRadius: 999, background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
        >
          <Link2 size={12} />
        </button>
        <div style={{ width: 52, height: 52, borderRadius: 999, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
          <PlayCircle size={26} style={{ color: '#fff' }} />
        </div>
      </div>
      <div style={{ padding: '13px 15px' }}>
        <div style={{ fontWeight: 600, fontSize: '0.88rem', lineHeight: 1.3 }}>{entry.title}</div>
        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 5, display: 'flex', alignItems: 'center', gap: 7 }}>
          Video · {fmtDate(entry.created_at)}
        </div>
      </div>
    </div>
  );
}

// ─── Link card — external links / driver & firmware downloads. No thumbnail or play affordance
// (nothing to preview) — the card body opens the URL in a new tab directly; a small "Details"
// button (same stopPropagation pattern as VideoTileCard's) reaches the edit/equipment-link modal
// instead. ────────────────────────────────────────────────────────────────────────────────────
function LinkCard({ entry, onOpen, onDetails }: { entry: LearningHubEntry; onOpen: () => void; onDetails: () => void }) {
  return (
    <div
      onClick={onOpen}
      style={{ borderRadius: 12, border: '1px solid var(--border-subtle)', background: 'rgba(255,255,255,0.02)', cursor: 'pointer', transition: 'all 0.16s', position: 'relative', padding: '16px 40px 16px 16px' }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.borderColor = 'var(--border-default)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = 'var(--border-subtle)'; }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onDetails(); }}
        title="Details / link equipment"
        style={{ position: 'absolute', top: 10, right: 10, width: 24, height: 24, borderRadius: 999, background: 'rgba(255,255,255,0.06)', border: 'none', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
      >
        <Link2 size={11} />
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 42, height: 42, borderRadius: 10, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(100,116,139,0.18)' }}>
          <ExternalLink size={19} style={{ color: '#94a3b8' }} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.title}</div>
          <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{urlDomain(entry.file_url)}</div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────────────────────
export default function LearningHubPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isManagerPlus = ['manager', 'org_admin', 'admin'].includes(user?.role ?? '');
  const isEnterprise = user?.orgPlanTier === 'enterprise' || user?.role === 'admin';

  const [search, setSearch] = useState('');
  const [activeType, setActiveType] = useState<EntryType | null>(null);
  // Drill-down stack of folder ids — mirrors Inventory.tsx's CategoryPicker pattern
  // (stack: number[], currentParentId = stack.length > 0 ? stack[stack.length - 1] : null).
  // Folder names for ids already visited are cached in folderNamesRef as they're encountered
  // (fetched level or opened tile), since folders are fetched per-level (scoped by parent_id)
  // rather than as one flat list, so breadcrumb path reconstruction can't just walk a fully
  // loaded array the way Inventory.tsx's getFullPath does.
  const [folderStack, setFolderStack] = useState<number[]>([]);
  const folderNamesRef = useRef<Map<number, string>>(new Map());
  const activeFolderId = folderStack.length > 0 ? folderStack[folderStack.length - 1] : null;
  const [formModal, setFormModal] = useState<{ mode: 'create' | 'edit'; entry?: LearningHubEntry } | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [lightbox, setLightbox] = useState<{ videoId: string; title: string; subtitle: string } | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [folderModal, setFolderModal] = useState<{ mode: 'create' | 'rename'; folder?: LearningHubFolder } | null>(null);
  const [folderDeleteTarget, setFolderDeleteTarget] = useState<LearningHubFolder | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const { data: entriesRes, isLoading } = useQuery({
    queryKey: ['learning-hub', activeType, search],
    queryFn: () => api.get<LearningHubEntry[]>('/learning-hub', {
      type: activeType ?? undefined,
      search: search.trim() || undefined,
    }),
  });
  const entries = entriesRes?.data ?? [];

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);

  // Selecting a category tile reveals its folders/entries below the hero + tiles, but the page
  // itself doesn't scroll there on its own -- the new content can render partially below the
  // viewport edge. Bring the results section fully into view whenever a category becomes active.
  //
  // Traced the actual sequence on a fresh page load: the entries query below is keyed on
  // ['learning-hub', activeType, search], so the FIRST time a given activeType is selected in a
  // session it's a genuine cache miss -- isLoading goes true and a real network round-trip has
  // to finish before the folders/cards render and the container reaches its final height. A
  // paint-timing fix (double requestAnimationFrame, tried previously) only waits a couple of
  // frames -- nowhere near long enough for that fetch -- so scrollIntoView was firing against a
  // container still showing the "Loading…" placeholder, landing short. On every later selection
  // the data is already cached, isLoading is already false, and the scroll lands correctly --
  // matching the reported "works from the second click onward" symptom exactly.
  //
  // Fix: gate the scroll on isLoading actually reaching false for the newly-selected category,
  // not on elapsed frames. scrollPendingRef is set only when activeType itself changes (never by
  // the search box, which shares the same query key and would otherwise toggle isLoading on
  // every keystroke and cause stray re-scrolls while a category is already open).
  const scrollPendingRef = useRef(false);

  function scrollResultsIntoView(): () => void {
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }

  useEffect(() => {
    if (!activeType) return;
    if (!isLoading) {
      // Already cached (e.g. re-selecting a previously-visited category) -- scroll now.
      return scrollResultsIntoView();
    }
    scrollPendingRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isLoading is read for its value at
    // the moment activeType changes, not as a reactive dependency; re-running this effect on
    // every isLoading toggle would re-arm scrollPendingRef on unrelated search-driven fetches.
  }, [activeType]);

  useEffect(() => {
    if (!scrollPendingRef.current || isLoading) return;
    scrollPendingRef.current = false;
    return scrollResultsIntoView();
  }, [isLoading]);

  const manuals = useMemo(() => entries.filter((e) => e.type === 'manual').slice(0, 6), [entries]);
  const videos = useMemo(() => entries.filter((e) => e.type === 'video').slice(0, 4), [entries]);

  // Folders are shown as sub-groups within a type's browse view (not the home preview sections,
  // and not while a search term is active — search cuts across folders, so results stay flat).
  const showFolders = !!activeType && !search.trim();
  // Scoped to the current stack position — parent_id omitted fetches top-level (server default),
  // otherwise fetches activeFolderId's direct children only (GET /learning-hub/folders?parent_id=).
  const { data: foldersRes } = useQuery({
    queryKey: ['learning-hub-folders', activeType, activeFolderId],
    queryFn: () => api.get<LearningHubFolder[]>('/learning-hub/folders', {
      type: activeType ?? undefined,
      parent_id: activeFolderId ?? undefined,
    }),
    enabled: !!activeType,
  });
  const folders = foldersRes?.data ?? [];
  useEffect(() => {
    folders.forEach((f) => folderNamesRef.current.set(f.id, f.name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folders]);
  const uncategorizedEntries = useMemo(() => entries.filter((e) => e.folder_id === null), [entries]);
  const folderEntries = useMemo(
    () => (activeFolderId !== null ? entries.filter((e) => e.folder_id === activeFolderId) : []),
    [entries, activeFolderId]
  );
  const currentLevelEntries = activeFolderId !== null ? folderEntries : uncategorizedEntries;

  function refreshList() {
    qc.invalidateQueries({ queryKey: ['learning-hub'] });
  }

  function refreshFolders() {
    qc.invalidateQueries({ queryKey: ['learning-hub-folders'] });
    // Same-class gap as the entries fix (refreshList/['learning-hub']): FolderPicker
    // (EntryFormModal's own Folder field, below in this file) fetches its OWN separate flat
    // "all folders for this type" cache under a DIFFERENT key -- ['learning-hub-folders-all',
    // type] -- for its search box, not ['learning-hub-folders', ...]. React Query's prefix/fuzzy
    // invalidation match only covers keys that literally START WITH the invalidated array, and
    // 'learning-hub-folders-all' is a different string, not a suffix of 'learning-hub-folders' --
    // so the line above never touched it. Without this, a folder created/renamed/deleted here
    // wouldn't show up (or would still show under its old name / still be selectable after
    // deletion) in the Add Entry form's Folder picker until a full page reload, even though the
    // main browse view above updates correctly.
    qc.invalidateQueries({ queryKey: ['learning-hub-folders-all'] });
  }

  function selectType(t: EntryType) {
    setActiveType((cur) => (cur === t ? null : t));
    setFolderStack([]);
  }

  function openFolder(f: LearningHubFolder) {
    folderNamesRef.current.set(f.id, f.name);
    setFolderStack((prev) => [...prev, f.id]);
  }

  function jumpFolderStack(depth: number) {
    setFolderStack((prev) => prev.slice(0, depth));
  }

  function openVideo(e: LearningHubEntry) {
    if (!e.youtube_video_id) return;
    setLightbox({ videoId: e.youtube_video_id, title: e.title, subtitle: `${TYPE_LABEL[e.type]} · ${fmtDate(e.created_at)}` });
  }

  function openLink(e: LearningHubEntry) {
    if (!e.file_url) return;
    window.open(e.file_url, '_blank', 'noopener,noreferrer');
  }

  function renderEntryGrid(list: LearningHubEntry[]) {
    if (activeType === 'video') {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {list.map((e) => <VideoTileCard key={e.id} entry={e} onPlay={() => openVideo(e)} onDetails={() => setDetailId(e.id)} />)}
        </div>
      );
    }
    if (activeType === 'link') {
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
          {list.map((e) => <LinkCard key={e.id} entry={e} onOpen={() => openLink(e)} onDetails={() => setDetailId(e.id)} />)}
        </div>
      );
    }
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
        {list.map((e) => <BookSpineCard key={e.id} entry={e} onClick={() => setDetailId(e.id)} />)}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1320, margin: '0 auto' }}>
      <div className="page-header" style={{ marginBottom: 20 }}>
        <div>
          <h2 className="page-title">Learning Hub</h2>
          <p className="page-subtitle">Equipment manuals, repair guides, and video walkthroughs for your facility.</p>
        </div>
      </div>

      {/* Hero + search */}
      <div style={{ borderRadius: 20, padding: '44px 40px', textAlign: 'center', background: 'linear-gradient(135deg, rgba(59,130,246,0.16), rgba(139,92,246,0.10) 55%, rgba(20,184,166,0.10))', border: '1px solid var(--border-subtle)', marginBottom: 26 }}>
        <h2 style={{ fontSize: '1.9rem', fontWeight: 800, letterSpacing: '-0.02em' }}>Find the manual you need</h2>
        <p style={{ color: 'var(--text-secondary)', marginTop: 6 }}>Search equipment manuals, repair guides, and video walkthroughs.</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, background: 'rgba(15,20,32,0.6)', border: '1px solid var(--border-default)', borderRadius: 11, padding: '12px 16px', maxWidth: 560, margin: '22px auto 0' }}>
          <Search size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search titles & manual content — e.g. “boiler manual” or “HVAC”…"
            style={{ flex: 1, background: 'none', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: '0.9rem' }}
          />
        </div>
      </div>

      {/* Category tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14, marginBottom: 28 }}>
        {CATEGORY_TILES.map((t) => (
          <div
            key={t.type}
            className="combo1"
            onClick={() => selectType(t.type)}
            onMouseLeave={spinIconOut}
            style={{
              borderRadius: 14, border: activeType === t.type ? '1px solid rgba(59,130,246,0.5)' : '1px solid var(--border-subtle)',
              background: activeType === t.type ? 'rgba(59,130,246,0.08)' : 'rgba(255,255,255,0.025)',
              padding: '14px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 14,
            }}
          >
            <div style={{ width: 40, height: 40, flexShrink: 0, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19, background: t.bg }}>
              <span className="btn-icon">{t.icon}</span>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{t.label}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <div ref={resultsRef}>
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>Loading…</div>
      ) : activeType ? (
        // Filtered browse — folder sub-groups within the selected type (Phase 4), or a flat grid
        // while a search term is active (search cuts across folders, so results stay flat).
        <>
          <div style={{ display: 'flex', alignItems: 'center', margin: '0 0 14px', gap: 12, flexWrap: 'wrap' }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 700 }}>
              {CATEGORY_TILES.find((t) => t.type === activeType)?.icon} {activeFolderId !== null ? (folderNamesRef.current.get(activeFolderId) ?? '…') : `${TYPE_LABEL[activeType]}s`}
            </h3>
            {activeFolderId !== null && (
              <span style={{ fontSize: '0.78rem', color: '#60a5fa', fontWeight: 600, cursor: 'pointer' }} onClick={() => setFolderStack([])}>
                ← All {TYPE_LABEL[activeType]}s
              </span>
            )}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16 }}>
              {isManagerPlus && showFolders && (
                <span
                  style={{ fontSize: '0.78rem', color: '#a78bfa', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                  onClick={() => setFolderModal({ mode: 'create' })}
                >
                  <FolderPlus size={13} /> New folder
                </span>
              )}
              <span
                style={{ fontSize: '0.78rem', color: '#60a5fa', fontWeight: 600, cursor: 'pointer' }}
                onClick={() => { setActiveType(null); setFolderStack([]); }}
              >
                Back to Learning Hub
              </span>
            </div>
          </div>

          {/* Breadcrumb — only when genuinely nested (more than one folder deep); the "← All Xs"
              link above already covers the single-level case. Segments before the last are
              clickable and jump the stack back to that depth (Inventory.tsx's getFullPath-style
              parent-chain walk, adapted to the per-level-fetched folderNamesRef cache since
              folders here aren't loaded as one flat list the way Inventory's categories are). */}
          {folderStack.length > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, fontSize: '0.78rem', color: 'var(--text-muted)', margin: '-6px 0 14px' }}>
              <span style={{ cursor: 'pointer', color: '#60a5fa' }} onClick={() => setFolderStack([])}>
                {TYPE_LABEL[activeType]}s
              </span>
              {folderStack.map((id, i) => (
                <span key={id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <span>›</span>
                  <span
                    style={{
                      cursor: i < folderStack.length - 1 ? 'pointer' : 'default',
                      color: i < folderStack.length - 1 ? '#60a5fa' : 'var(--text-secondary)',
                      fontWeight: i === folderStack.length - 1 ? 600 : 400,
                    }}
                    onClick={() => { if (i < folderStack.length - 1) jumpFolderStack(i + 1); }}
                  >
                    {folderNamesRef.current.get(id) ?? '…'}
                  </span>
                </span>
              ))}
            </div>
          )}

          {showFolders ? (
            // Folder tiles (subfolders of the current stack position) as sub-groups, entries
            // directly in the current folder shown below — "Uncategorized" label only applies
            // at the type root, where activeFolderId is null.
            folders.length === 0 && currentLevelEntries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
                {activeFolderId !== null ? 'This folder is empty.' : `No ${TYPE_LABEL[activeType].toLowerCase()}s yet.`}
              </div>
            ) : (
              <>
                {folders.length > 0 && (
                  <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${activeType === 'video' ? 280 : 200}px, 1fr))`, gap: 16, marginBottom: 24 }}>
                    {folders.map((f) => (
                      <FolderTile
                        key={f.id}
                        folder={f}
                        type={activeType}
                        previewVideoId={activeType === 'video' ? entries.find((e) => e.folder_id === f.id)?.youtube_video_id : undefined}
                        canManage={isManagerPlus}
                        onOpen={() => openFolder(f)}
                        onRename={() => setFolderModal({ mode: 'rename', folder: f })}
                        onDelete={() => setFolderDeleteTarget(f)}
                      />
                    ))}
                  </div>
                )}
                {currentLevelEntries.length > 0 && (
                  <>
                    {activeFolderId === null && folders.length > 0 && (
                      <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 12 }}>
                        Uncategorized
                      </div>
                    )}
                    {renderEntryGrid(currentLevelEntries)}
                  </>
                )}
              </>
            )
          ) : (
            // Search active — flat results across the whole type, folders not applied.
            entries.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>No {TYPE_LABEL[activeType].toLowerCase()}s yet.</div>
            ) : renderEntryGrid(entries)
          )}
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', margin: '26px 0 14px' }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 700 }}>📘 Popular manuals</h3>
            {manuals.length > 0 && <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#60a5fa', fontWeight: 600, cursor: 'pointer' }} onClick={() => selectType('manual')}>View all →</span>}
          </div>
          {manuals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: '0.85rem', fontStyle: 'italic' }}>No manuals yet.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
              {manuals.map((e) => <BookSpineCard key={e.id} entry={e} onClick={() => setDetailId(e.id)} />)}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', margin: '26px 0 14px' }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 700 }}>🎬 Latest videos</h3>
            {videos.length > 0 && <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: '#60a5fa', fontWeight: 600, cursor: 'pointer' }} onClick={() => selectType('video')}>View all →</span>}
          </div>
          {videos.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: '0.85rem', fontStyle: 'italic' }}>No videos yet.</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {videos.map((e) => <VideoTileCard key={e.id} entry={e} onPlay={() => openVideo(e)} onDetails={() => setDetailId(e.id)} />)}
            </div>
          )}
        </>
      )}
      </div>

      {lightbox && (
        <VideoLightbox videoId={lightbox.videoId} title={lightbox.title} subtitle={lightbox.subtitle} onClose={() => setLightbox(null)} />
      )}

      {formModal && (
        <EntryFormModal
          mode={formModal.mode}
          initial={formModal.entry}
          onClose={() => setFormModal(null)}
          onSaved={() => {
            setFormModal(null);
            setDetailId(null);
            refreshList();
            setToast(formModal.mode === 'create' ? 'Entry added' : 'Entry updated');
          }}
        />
      )}

      {detailId !== null && (
        <EntryDetailModal
          entryId={detailId}
          canManage={isManagerPlus}
          onClose={() => setDetailId(null)}
          onEdit={(e) => setFormModal({ mode: 'edit', entry: e })}
          onDeleted={() => { setDetailId(null); setToast('Entry deleted'); }}
          onPlayVideo={openVideo}
        />
      )}

      {folderModal && activeType && (
        <FolderModal
          mode={folderModal.mode}
          type={activeType}
          initial={folderModal.folder}
          parentId={activeFolderId}
          onClose={() => setFolderModal(null)}
          onSaved={() => {
            setFolderModal(null);
            refreshFolders();
            setToast(folderModal.mode === 'create' ? 'Folder created' : 'Folder renamed');
          }}
        />
      )}

      {folderDeleteTarget && (
        <ConfirmModal
          title="Delete folder?"
          message={
            folderDeleteTarget.subfolder_count > 0
              ? `"${folderDeleteTarget.name}" and its ${folderDeleteTarget.subfolder_count} subfolder${folderDeleteTarget.subfolder_count === 1 ? '' : 's'} will be removed. Entries directly inside any of them aren't deleted — they'll move back to Uncategorized.`
              : `"${folderDeleteTarget.name}" will be removed. Its entries aren't deleted — they'll move back to Uncategorized.`
          }
          confirmLabel="Delete"
          danger
          onConfirm={async () => {
            await api.delete(`/learning-hub/folders/${folderDeleteTarget.id}`);
            // Deleting a folder cascades to its whole subfolder tree (migration_v121) — if the
            // deleted folder is anywhere in the current drill-down stack, pop back to just before it.
            const idx = folderStack.indexOf(folderDeleteTarget.id);
            if (idx !== -1) setFolderStack((prev) => prev.slice(0, idx));
            refreshFolders();
            refreshList();
            setFolderDeleteTarget(null);
            setToast('Folder deleted');
          }}
          onClose={() => setFolderDeleteTarget(null)}
        />
      )}

      {isManagerPlus && (
        <FloatingActionButton
          icon={<Upload size={20} className="btn-icon" />}
          onClick={() => setFormModal({ mode: 'create' })}
          title="Upload to Learning Hub"
          ariaLabel="Upload to Learning Hub"
          right={isEnterprise ? 98 : 28}
        />
      )}

      {isEnterprise && <AiChatWidget />}

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9998, background: '#064e3b', color: '#10b981', border: '1px solid #10b981', borderRadius: 8, padding: '10px 18px', fontWeight: 600, fontSize: '0.875rem', boxShadow: '0 4px 20px rgba(0,0,0,0.4)' }}>
          {toast}
        </div>
      )}
    </div>
  );
}
